/**
 * ValveMan Valve Finder — headless engine (no DOM rendering here).
 *
 * The wizard never holds a product catalogue. Every count comes from the real
 * collection page's Search & Discovery facets, fetched same-origin through the
 * Section Rendering API (sections/vm-facet-json.liquid), and the wizard's final
 * answer is the real filtered collection URL the shopper lands on.
 *
 * Loaded in the browser as window.VMValveFinderEngine, and by node --test via
 * module.exports (scripts/valve-finder-engine.test.mjs). Plain ES2017, no deps.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.VMValveFinderEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* Facet params. Namespace is specs (NOT custom) for every spec facet. */
  var PARAMS = {
    size: 'filter.p.m.specs.size',
    end_connection: 'filter.p.m.specs.end_connection',
    body_material: 'filter.p.m.specs.body_material',
    operation: 'filter.p.m.specs.operation',
    check_type: 'filter.p.m.specs.check_type',
    vendor: 'filter.p.vendor',
    is_series_parent: 'filter.p.m.custom.is_series_parent'
  };

  /* Facet answer keys in the fixed order they are written into a URL, so one set
     of answers always produces one byte-identical query (cache key + deep link). */
  var FACET_KEYS = ['size', 'end_connection', 'body_material', 'check_type', 'operation'];

  var SECTION_ID = 'vm-facet-json';
  var TIMEOUT_MS = 4000;

  /* ---------- small utilities ---------- */

  /* Liquid hands filter values back HTML-escaped (1/2" arrives as 1/2&quot;).
     They must be decoded before they go into a URL or onto the screen. */
  function decodeEntities(s) {
    if (s == null) return s;
    return String(s).replace(/&(#x[0-9a-f]+|#\d+|quot|amp|lt|gt|apos|#39);/gi, function (m, e) {
      var k = e.toLowerCase();
      if (k === 'quot') return '"';
      if (k === 'amp') return '&';
      if (k === 'lt') return '<';
      if (k === 'gt') return '>';
      if (k === 'apos' || k === '#39') return "'";
      if (k.charAt(1) === 'x') return String.fromCharCode(parseInt(k.slice(2), 16));
      return String.fromCharCode(parseInt(k.slice(1), 10));
    });
  }

  /* Answers → ordered [param, value] pairs. `parentsOnly` mirrors what the
     collection page itself forces (category-products.liquid redirects any URL
     without is_series_parent to =1), so a prediction is always measured under the
     same filter the destination shows. Gil, Sep 30 2026: include =1. */
  function buildPairs(answers, opts) {
    var pairs = [];
    answers = answers || {};
    FACET_KEYS.forEach(function (k) {
      var v = answers[k];
      if (v == null || v === '') return;
      (Array.isArray(v) ? v : [v]).forEach(function (one) { pairs.push([PARAMS[k], one]); });
    });
    if (!opts || opts.parentsOnly !== false) pairs.push([PARAMS.is_series_parent, '1']);
    return pairs;
  }

  function encodePairs(pairs) {
    return pairs.map(function (p) {
      return encodeURIComponent(p[0]) + '=' + encodeURIComponent(p[1]).replace(/%20/g, '+');
    }).join('&');
  }

  /* A collection handle the wizard may build a URL from. The controller drops any
     type that fails this at boot, so collectionPath's throw is a last line of defence. */
  function validHandle(handle) { return /^[a-z0-9][a-z0-9-]*$/.test(handle || ''); }

  function collectionPath(handle) {
    if (!validHandle(handle)) throw new Error('Valve Finder: bad collection handle ' + handle);
    return '/collections/' + handle;
  }

  /* The destination: the real collection page with answers as removable filters. */
  function resultsUrl(handle, answers, opts) {
    var q = encodePairs(buildPairs(answers, opts));
    return collectionPath(handle) + (q ? '?' + q : '');
  }

  function facetUrl(handle, answers, opts) {
    var q = encodePairs(buildPairs(answers, opts));
    return collectionPath(handle) + '?section_id=' + SECTION_ID + (q ? '&' + q : '');
  }

  /* Section Rendering API response → normalised facet data.
     Returns null when the payload is missing or malformed (never a fake 0). */
  function parseFacetHtml(html) {
    var m = /<script type="application\/json" data-vm-facet-json>([\s\S]*?)<\/script>/.exec(html || '');
    if (!m) return null;
    var raw;
    try { raw = JSON.parse(m[1]); } catch (e) { return null; }
    if (!raw || typeof raw.total !== 'number' || !Array.isArray(raw.filters)) return null;
    var byKey = {};
    raw.filters.forEach(function (f) {
      var key = null;
      Object.keys(PARAMS).forEach(function (k) { if (PARAMS[k] === f.param) key = k; });
      if (!key) return;
      byKey[key] = (f.values || []).map(function (v) {
        return { value: decodeEntities(v.value), label: decodeEntities(v.label), count: typeof v.count === 'number' ? v.count : null };
      });
    });
    return { handle: raw.handle, total: raw.total, facets: byKey };
  }

  /* ---------- count client: cache, abort, timeout ---------- */

  function createCountClient(opts) {
    opts = opts || {};
    var fetchImpl = opts.fetch || function (u, i) { return fetch(u, i); };
    var timeoutMs = opts.timeoutMs || TIMEOUT_MS;
    var onError = opts.onError || function () {};
    var cache = {};          // url → parsed facet data (successful responses only)
    var inflight = {};       // url → promise, so a prefetch and a real ask share one request
    var current = null;      // AbortController for the latest foreground request

    /* One network request per URL, shared by every caller. It never times out itself:
       a slow-but-working answer still lands in the cache, so the next ask is instant.
       Resolves the parsed data, or null on HTTP error / unparseable body / abort. */
    function request(url, signal) {
      if (inflight[url]) return inflight[url];
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      if (signal && ctrl) {
        if (signal.aborted) ctrl.abort();
        else signal.addEventListener('abort', function () { ctrl.abort(); });
      }
      var p = fetchImpl(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (html) {
          var data = parseFacetHtml(html);
          if (!data) throw new Error('unparseable facet payload');
          cache[url] = data;
          return data;
        })
        .catch(function (err) {
          if (!(err && err.name === 'AbortError')) onError({ url: url, error: String(err && err.message || err) });
          return null;
        })
        .then(function (v) {
          // Clean up BEFORE the caller sees it: a retry right after a failure must not
          // be handed this finished promise from the in-flight map.
          if (inflight[url] === p) delete inflight[url];
          return v;
        });
      inflight[url] = p;
      return p;
    }

    /* Each CALLER gets its own timeout, measured from its own ask: a foreground ask that
       joins a prefetch started 3.9s earlier still waits its full timeoutMs. On timeout the
       caller gets null (no counts, never a fake zero); the request keeps running. */
    function load(url, signal) {
      if (cache[url]) return Promise.resolve(cache[url]);
      var shared = request(url, signal);
      return new Promise(function (resolve) {
        var settled = false;
        var timer = setTimeout(function () {
          if (settled) return;
          settled = true;
          onError({ url: url, error: 'timeout' });
          // A stalled fetch may never settle. Stop sharing it, so the next ask starts a
          // fresh request instead of joining a dead one; if the slow one does answer,
          // it still fills the cache.
          if (inflight[url] === shared) delete inflight[url];
          resolve(null);
        }, timeoutMs);
        shared.then(function (v) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(v);
        });
      });
    }

    return {
      /* Foreground request: a newer call for a DIFFERENT url aborts the previous one.
         Resolves null on failure/timeout/abort — callers render "no counts", never zeros. */
      get: function (handle, answers, o) {
        var url = facetUrl(handle, answers, o);
        if (cache[url]) return Promise.resolve(cache[url]);
        if (!inflight[url]) {                         // not already asked (prefetch or a repeat)
          if (current) current.abort();
          current = typeof AbortController !== 'undefined' ? new AbortController() : null;
        }
        return load(url, current ? current.signal : null);
      },
      /* Background warm-up for the next question; never aborted by get(). */
      prefetch: function (handle, answers, o) { return load(facetUrl(handle, answers, o)); },
      peek: function (handle, answers, o) { return cache[facetUrl(handle, answers, o)] || null; },
      _cache: cache
    };
  }

  /* =====================================================================
     PHASE 2 — question engine (pure functions over a plain state object)
     ===================================================================== */

  /* Question order (reviewed). 'media' only exists when the section's feature flag
     is on; 'check_type' only for Check valves. */
  var STEP_ORDER = ['type', 'size', 'end_connection', 'media', 'body_material', 'check_type', 'operation'];

  /* A facet question is asked only when the data can carry it:
     • at least MIN_VALUES populated values, AND
     • populated values cover at least MIN_COVERAGE of the products in play.
     The coverage floor is what skips butterfly connection (2 values, but only 3 of 208
     families carry one) and globe connection (6 of 24). Measured Sep 30 2026: the
     nearest asked case is check_type at 42%, the nearest skipped is globe at 25%.
     Added beyond the original spec; endorsed by Gil Oct 1 2026. The value is pinned
     by a boundary test (35% asks, 34.5% skips): change it deliberately, with the test. */
  var MIN_VALUES = 2;
  var MIN_COVERAGE = 0.35;

  /* Coverage = share of products in play that carry ANY value of the facet. Facet
     counts cannot give that directly: spec facets are LIST metafields, so one product
     with three sizes adds to three counts. They do bound it:
       floor   = largest single value count / total  (those products all carry a value)
       ceiling = sum of value counts / total          (every product counted once per value)
     If 35% falls between the two, the caller fetches the exact union (every populated
     value selected; S&D ORs values within a facet) and passes its total as `union`. */
  function populatedValues(key, data) {
    return (((data && data.facets) || {})[key] || []).filter(function (v) { return v.count > 0; }).map(function (v) { return v.value; });
  }

  function evaluateFacet(key, data, union) {
    if (!data) return { ask: true, known: false };
    var vals = (data.facets && data.facets[key]) || [];
    var populated = vals.filter(function (v) { return v.count > 0; });
    if (!populated.length) return { ask: false, known: true, reason: 'no-data' };
    if (populated.length < MIN_VALUES) return { ask: false, known: true, reason: 'one-value' };
    if (!(data.total > 0)) return { ask: false, known: true, reason: 'no-data' };
    var coverage;
    // The union of every value can never be smaller than its largest member: a smaller
    // number is a stale or partial response, so clamp rather than skip a real question.
    var biggest = Math.max.apply(null, populated.map(function (v) { return v.count; }));
    if (typeof union === 'number') coverage = Math.max(union, biggest) / data.total;
    else {
      var floor = biggest / data.total;
      var ceiling = populated.reduce(function (s, v) { return s + v.count; }, 0) / data.total;
      if (ceiling < MIN_COVERAGE) coverage = ceiling;
      else if (floor >= MIN_COVERAGE) coverage = floor;
      else return { ask: null, known: true, needsUnion: true, values: populated.map(function (v) { return v.value; }) };
    }
    if (coverage < MIN_COVERAGE) return { ask: false, known: true, reason: 'low-coverage', coverage: coverage };
    return { ask: true, known: true, coverage: coverage };
  }

  /* The question plan for one valve type, from that type's facet data with no
     answers applied. Returns every step after 'type' with ask + reason. */
  /* opts.unions: { facetKey: exact union total } for facets evaluateFacet marked
     needsUnion. A still-unresolved facet (union fetch failed) is ASKED: unknown
     coverage never hides a question. */
  function planFor(typeKey, data, opts) {
    opts = opts || {};
    var unions = opts.unions || {};
    return STEP_ORDER.slice(1).filter(function (k) {
      if (k === 'media') return !!opts.mediaEnabled;
      if (k === 'check_type') return typeKey === 'check';
      return true;
    }).map(function (k) {
      if (k === 'media') return { key: k, ask: true };
      var e = evaluateFacet(k, data, unions[k]);
      return { key: k, ask: e.ask !== false, reason: e.ask === false ? e.reason : null };
    });
  }

  /* ---------- sizes: a numeric scale, never a popularity grid ---------- */

  /* '1/2"' → 0.5, '1-1/4"' (or '1 1/4"') → 1.25, '2"' → 2, '1.5"' → 1.5,
     '7/16"' → 0.4375. Anything else (pairs, ranges, metric) → null. */
  function parseInches(s) {
    var m = /^\s*(?:(\d+)(?:[- ](\d+)\/(\d+))?|(\d+)\/(\d+)|(\d*\.\d+))\s*"\s*$/.exec(s || '');
    if (!m) return null;
    if (m[6]) return Number(m[6]);
    if (m[4]) return Number(m[4]) / Number(m[5]);
    var whole = Number(m[1]);
    return m[2] ? whole + Number(m[2]) / Number(m[3]) : whole;
  }

  /* First number in a pair/range ('2-1/2" to 5"' → 2.5) — only used to sort that row. */
  function leadingInches(s) {
    var m = /(\d+(?:[- ]\d+\/\d+)?"|\d+\/\d+"|\d*\.\d+")/.exec(s || '');
    return m ? parseInches(m[1]) : Infinity;
  }

  /* Bands match their labels exactly. A single size between bands (2-1/4", 7")
     goes to an 'other' band rather than under a heading that excludes it. */
  var SIZE_BANDS = [
    { key: 'under-1', test: function (x) { return x < 1; } },
    { key: '1-to-2', test: function (x) { return x >= 1 && x <= 2; } },
    { key: '2h-to-6', test: function (x) { return x >= 2.5 && x <= 6; } },
    { key: '8-plus', test: function (x) { return x >= 8; } }
  ];

  /* values: [{value,label,count}] from the facet. countsKnown: whether counts are
     real (a failed fetch passes false → every size stays enabled). Counts are never
     exposed for sizes (Gil): items carry only `disabled`. */
  function sizeScale(values, countsKnown) {
    var singles = [], multi = [];
    (values || []).forEach(function (v) {
      var x = parseInches(v.value);
      var item = { value: v.value, label: v.label, disabled: !!countsKnown && !(v.count > 0) };
      if (x == null) { item.sortKey = leadingInches(v.value); multi.push(item); }
      else { item.inches = x; singles.push(item); }
    });
    singles.sort(function (a, b) { return a.inches - b.inches; });
    multi.sort(function (a, b) { return a.sortKey - b.sortKey || (a.value < b.value ? -1 : 1); });
    var bands = SIZE_BANDS.map(function (b) {
      return { key: b.key, items: singles.filter(function (i) { return b.test(i.inches); }) };
    });
    var other = singles.filter(function (i) { return !SIZE_BANDS.some(function (b) { return b.test(i.inches); }); });
    bands.push({ key: 'other', items: other });
    bands = bands.filter(function (b) { return b.items.length; });
    return { bands: bands, multi: multi };
  }

  /* ---------- other questions: popularity, with pressure classes kept together ---------- */

  function classRank(label) {
    var m = /^(\d+)#/.exec(label || '') || /^Class\s+(\d+)/i.exec(label || '');
    return m ? Number(m[1]) : null;
  }

  /* Most popular first by live count; zero-count options sink to the bottom,
     disabled. Pressure/flange classes (150#, 300#… / Class 150…) form ONE contiguous
     ascending block, placed where its most popular member would sit. With counts
     unknown, the facet's own order is kept and everything stays enabled. */
  function orderOptions(values, countsKnown) {
    var list = (values || []).map(function (v, i) {
      return { value: v.value, label: v.label, count: countsKnown ? (v.count || 0) : null, idx: i, rank: classRank(v.label) };
    });
    if (!countsKnown) return list.map(function (o) { o.disabled = false; return o; });
    var live = list.filter(function (o) { return o.count > 0; });
    var dead = list.filter(function (o) { return !(o.count > 0); });
    live.sort(function (a, b) { return b.count - a.count || a.idx - b.idx; });
    var classes = live.filter(function (o) { return o.rank != null; }).sort(function (a, b) { return a.rank - b.rank || a.idx - b.idx; });
    var out = [], placed = false;
    live.forEach(function (o) {
      if (o.rank == null) { out.push(o); return; }
      if (!placed) { placed = true; out = out.concat(classes); }
    });
    dead.sort(function (a, b) { return a.idx - b.idx; });
    return out.map(function (o) { o.disabled = false; return o; })
      .concat(dead.map(function (o) { o.disabled = true; return o; }));
  }

  /* ---------- wizard state transitions (pure) ---------- */

  function initialState() {
    return { type: null, plan: [], basePlan: [], answers: {}, any: {}, media: null, current: 'type', resumeAt: null };
  }

  function clone(s) {
    return {
      type: s.type, plan: copyPlan(s.plan), basePlan: copyPlan(s.basePlan || []),
      answers: Object.assign({}, s.answers), any: Object.assign({}, s.any), media: s.media, current: s.current,
      resumeAt: s.resumeAt || null
    };
  }

  function copyPlan(plan) {
    return plan.map(function (p) { return { key: p.key, ask: p.ask, reason: p.reason || null }; });
  }

  function askedSteps(s) {
    return ['type'].concat(s.plan.filter(function (p) { return p.ask; }).map(function (p) { return p.key; }));
  }

  function nextStep(s, from) {
    var steps = askedSteps(s), i = steps.indexOf(from);
    return i >= 0 && i < steps.length - 1 ? steps[i + 1] : 'results';
  }

  function prevStep(s, from) {
    var steps = askedSteps(s);
    if (from === 'results') return steps[steps.length - 1];
    var i = steps.indexOf(from);
    return i > 0 ? steps[i - 1] : null;
  }

  /* Forget every answer that comes after `key` in the question order, and undo any
     runtime narrowing (skipNow) of those later steps: a changed earlier answer can
     make a narrowed-away question meaningful again. */
  function clearAfter(s, key) {
    var i = STEP_ORDER.indexOf(key);
    var later = STEP_ORDER.slice(i + 1);
    later.forEach(function (k) {
      delete s.answers[k]; delete s.any[k];
      if (k === 'media') s.media = null;
    });
    if (key === 'type') { s.plan = []; s.basePlan = []; return s; }
    s.plan = s.plan.map(function (p) {
      if (later.indexOf(p.key) < 0) return p;
      var b = s.basePlan.filter(function (x) { return x.key === p.key; })[0];
      return b ? { key: b.key, ask: b.ask, reason: b.reason || null } : p;
    });
    return s;
  }

  function selectType(state, typeKey, plan) {
    var s = clearAfter(clone(state), 'type');
    s.type = typeKey;
    s.plan = copyPlan(plan);
    s.basePlan = copyPlan(plan);
    s.current = nextStep(s, 'type');
    return s;
  }

  function answer(state, key, value) {
    var s = clearAfter(clone(state), key);
    if (key === 'media') s.media = value;
    else { s.answers[key] = value; delete s.any[key]; }
    s.current = nextStep(s, key);
    return s;
  }

  /* "Not sure, show me every X": leaves the dimension unconstrained. */
  function leaveOpen(state, key) {
    var s = clearAfter(clone(state), key);
    delete s.answers[key];
    if (key === 'media') s.media = null;
    else s.any[key] = true;
    s.current = nextStep(s, key);
    return s;
  }

  /* A question the plan expected but that current answers have narrowed away
     (e.g. only one material left). Recorded so the handoff can say why. */
  function skipNow(state, key, reason) {
    var s = clone(state);
    s.plan.forEach(function (p) { if (p.key === key) { p.ask = false; p.reason = reason; } });
    delete s.answers[key]; delete s.any[key];
    s.current = nextStep(s, prevStep(state, key) || 'type');
    return s;
  }

  /* Jump back to a step (chip click / Back). Answers after it are cleared; its own
     answer is kept so it shows as selected. */
  function goTo(state, key) {
    if (key === 'type') return initialState();   // a type chip starts over: no stale progress or URL
    var s = clone(state);
    s.resumeAt = null;   // goTo is the only way off results, so this is the one reset needed
    if (key !== 'results') clearAfter(s, key);
    // Skip ahead from a question: Back on results returns to THAT question, not to the
    // last planned one the shopper never saw.
    else if (state.current !== 'results' && state.current !== 'type') s.resumeAt = state.current;
    s.current = key;
    return s;
  }

  /* Zero-results recovery: drop one answer (it becomes "left open"), stay on results. */
  function relax(state, key) {
    var s = clone(state);
    if (key === 'media') s.media = null;
    else { delete s.answers[key]; s.any[key] = true; }
    // Dropping an answer can reopen questions narrowed away after it: back to the
    // base plan for every later step, so no stale "only one option fits" remains.
    var later = STEP_ORDER.slice(STEP_ORDER.indexOf(key) + 1);
    s.plan = s.plan.map(function (p) {
      if (later.indexOf(p.key) < 0) return p;
      var b = s.basePlan.filter(function (x) { return x.key === p.key; })[0];
      return b ? { key: b.key, ask: b.ask, reason: b.reason || null } : p;
    });
    return s;
  }

  function back(state) {
    var p = state.current === 'results' && state.resumeAt && askedSteps(state).indexOf(state.resumeAt) >= 0
      ? state.resumeAt : prevStep(state, state.current);
    return p ? goTo(state, p) : state;
  }

  /* Chips = answered (or left open) steps before the current one. */
  function chips(state) {
    var steps = askedSteps(state), stop = state.current === 'results' ? steps.length : steps.indexOf(state.current);
    return steps.slice(0, stop < 0 ? steps.length : stop).map(function (k) {
      if (k === 'type') return { key: k, value: state.type };
      if (k === 'media') return { key: k, value: state.media, any: state.media == null };
      return { key: k, value: state.answers[k] == null ? null : state.answers[k], any: !!state.any[k] };
    }).filter(function (c) { return c.value != null || c.any; });
  }

  /* Questions not asked for this type + why, and dimensions left open. */
  function summary(state) {
    return {
      skipped: state.plan.filter(function (p) { return !p.ask && p.key !== 'media'; }).map(function (p) { return { key: p.key, reason: p.reason }; }),
      open: Object.keys(state.any).filter(function (k) { return state.any[k]; })
    };
  }

  /* ---------- URL state for /pages/valve-finder (history.replaceState) ---------- */

  var QS_KEYS = { type: 't', size: 'size', end_connection: 'conn', body_material: 'mat', check_type: 'ctype', operation: 'op', media: 'media' };
  var ANY = '*';

  function serialize(state) {
    var out = [];
    if (state.type) out.push([QS_KEYS.type, state.type]);
    STEP_ORDER.slice(1).forEach(function (k) {
      if (k === 'media') { if (state.media) out.push([QS_KEYS.media, state.media]); return; }
      if (state.any[k]) out.push([QS_KEYS[k], ANY]);
      else if (state.answers[k] != null) out.push([QS_KEYS[k], state.answers[k]]);
    });
    return encodePairs(out);
  }

  /* Returns raw restored answers. The caller validates each value against live
     facet data before trusting it (a stale link must not force a zero-result URL). */
  function deserialize(search, typeKeys) {
    var q = new URLSearchParams(search || '');
    var t = q.get(QS_KEYS.type);
    var r = { type: typeKeys.indexOf(t) >= 0 ? t : null, answers: {}, any: {}, media: null };
    if (!r.type) return r;
    FACET_KEYS.forEach(function (k) {
      var v = q.get(QS_KEYS[k]);
      if (v == null || v === '') return;
      if (v === ANY) r.any[k] = true; else r.answers[k] = v;
    });
    var m = q.get(QS_KEYS.media);
    if (m) r.media = m;
    return r;
  }

  return {
    STEP_ORDER: STEP_ORDER,
    MIN_VALUES: MIN_VALUES,
    MIN_COVERAGE: MIN_COVERAGE,
    evaluateFacet: evaluateFacet,
    populatedValues: populatedValues,
    QS_KEYS: QS_KEYS,
    planFor: planFor,
    parseInches: parseInches,
    sizeScale: sizeScale,
    classRank: classRank,
    orderOptions: orderOptions,
    initialState: initialState,
    askedSteps: askedSteps,
    nextStep: nextStep,
    prevStep: prevStep,
    selectType: selectType,
    answer: answer,
    leaveOpen: leaveOpen,
    skipNow: skipNow,
    goTo: goTo,
    back: back,
    relax: relax,
    chips: chips,
    summary: summary,
    serialize: serialize,
    deserialize: deserialize,
    PARAMS: PARAMS,
    FACET_KEYS: FACET_KEYS,
    SECTION_ID: SECTION_ID,
    TIMEOUT_MS: TIMEOUT_MS,
    decodeEntities: decodeEntities,
    buildPairs: buildPairs,
    validHandle: validHandle,
    resultsUrl: resultsUrl,
    facetUrl: facetUrl,
    parseFacetHtml: parseFacetHtml,
    createCountClient: createCountClient
  };
});
